import React, { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

// ANT DESIGN IMPORTS
import { ReloadOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  InputNumber,
  Row,
  Select,
  Spin,
  Table,
  Typography,
} from "antd";

// PROJECT IMPORTS
import styles from "../../../assets/styles/styles";
import CustomButton from "../../../components/custom/CustomButton";
import DownloadTableButtons from "../../../components/DownloadTableButtons";
import MainLayout from "../../../components/layouts/MainLayout";
import TopContent from "../../../components/layouts/TopContent";
import { rightAction } from "../../../redux/actions";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { apiConfig, labelConfig, staticConfig } from "../security.config";
import SortableColumn from "../../../components/SorterTableColumn";

const rowSelection = {
  onChange: (selectedRowKeys, selectedRows) => {},
  getCheckboxProps: (record) => ({
    disabled: record.name === "Disabled User",
    name: record.name,
  }),
};

const columns = [
  {
    title: labelConfig.id,
    dataIndex: "id",
  },
  {
    title: labelConfig.resourceId,
    dataIndex: "resourceId",
  },
  SortableColumn({
    label: labelConfig.branch,
    dataIndex: "branch",
    sorter: (a, b) => a.branch.localeCompare(b.branch),
  }),
  {
    title: labelConfig.status,
    dataIndex: "status",
  },
  SortableColumn({
    label: labelConfig.madeBy,
    dataIndex: "madeBy",
    sorter: (a, b) => a.madeBy.localeCompare(b.madeBy),
  }),
  {
    title: labelConfig.entity,
    dataIndex: "entity",
  },
  {
    title: labelConfig.timeStamp,
    dataIndex: "timeStamp",
  },
  {
    title: labelConfig.madeDate,
    dataIndex: "madeDate",
  },
  SortableColumn({
    label: labelConfig.checker,
    dataIndex: "checker",
    sorter: (a, b) => a.checker.localeCompare(b.checker),
  }),
  {
    title: labelConfig.checkedDate,
    dataIndex: "checkedDate",
  },
  {
    title: labelConfig.action,
    dataIndex: "action",
  },
];

const SearchAuditTrails = (props) => {
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;
  const { Text, Title } = Typography;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [useForm] = Form.useForm();

  // STATE VARIABLES
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [limit, setLimit] = useState(15);
  const [auditTrailList, setAuditTrailList] = useState([]);
  const [originalList, setOriginalList] = useState([]);
  const [appUserData, setAppUserData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [entityData, setEntityData] = useState([]);
  const [actionData, setActionData] = useState([]);
  const [showSearch, setShowSearch] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [offset, setOffset] = useState(0);
  // const limit = 14;
  const [totalRecords, setTotalRecords] = useState(0);

  function selectTableCount(value) {
    setLimit(value);
  }

  useEffect(() => {
    getSearchTemplate();
  }, []);

  function getSearchTemplate() {
    const url = `${apiConfig.Audits}/searchtemplate`;
    let body = { session };

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err);
      } else {
        const objList = res;

        let tempUsersData = [];
        let tempActionData = [];
        let tempEntityData = [];
        let tempStatusData = [];

        objList?.appUsers?.forEach((element) => {
          tempUsersData.push({
            value: element.id,
            label: element.username,
          });
        });

        objList?.actionNames?.forEach((element, index) => {
          tempActionData.push({
            value: index,
            label: element,
          });
        });

        objList?.entityNames?.forEach((element, index) => {
          tempEntityData.push({
            value: index,
            label: element,
          });
        });

        objList?.processingResults?.forEach((element) => {
          tempStatusData.push({
            value: element.id,
            label: element.processingResult,
          });
        });

        setAppUserData(tempUsersData);
        setActionData(tempActionData);
        setEntityData(tempEntityData);
        setStatusData(tempStatusData);
      }
    });

    setIsLoading(false);
  }

  // FLOAT METHODS
  function handleOnView() {
    delete selectedRow.amount;
    selectedRow.amount = selectedRow.amountToUse;
    const { detailBtn } = rightBtn;
    if (detailBtn.toLowerCase() === "primary") {
      navigate("/detail-audit-trail", { state: selectedRow });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }

  const onResetFields = () => {
    useForm.resetFields();
  };

  const onSelectItemAction = (selectedRowKeys, objParam) => {
    setSelectedRow(objParam[0]);
    [cnt.RIGHT_DETAIL_BTN].map((item) => {
      dispatch({
        type: item,
      });
    });
  };

  // Store the search parameters in state so they persist for pagination
  const [searchParams, setSearchParams] = useState({});

  // Helper function: make API call with given offset and search parameters
  const fetchSearchAudit = (currentOffset, params) => {
    setIsLoading(true);
    const url = `${apiConfig.Audits}`;
    // Override the offset in the params for the current API call
    const updatedParams = BOM.omitNullishParams({
      ...params,
      offset: currentOffset,
      limit,
      paged: true,
    });

    const body = { session, params: updatedParams };

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
      } else {
        const objList = res.pageItems || [];
        const tempArray = objList.map((element) => ({
          key: element?.id,
          id: element?.id || "-",
          resourceId: element?.resourceId || "-",
          branch: element?.officeName || "-",
          status: element?.processingResult || "-",
          madeBy: element?.maker || "-",
          action: element?.actionName || "-",
          madeDate: element?.madeOnDate ? BOM.DateTime(element?.madeOnDate, "only-date") : "-",
          timeStamp: element?.madeOnDate ? BOM.DateTime(element?.madeOnDate, "with-seconds") : "-",
          entity: element?.entityName || "-",
          checker: element?.checker || "-",
          checkedDate: element?.checkedOnDate
            ? BOM.DateTime(element?.checkedOnDate, "only-date")
            : "-",
        }));

        setAuditTrailList(tempArray);
        setOriginalList(tempArray);

        // If the returned record count is less than the limit,
        // we assume there are no more records.
        if (objList.length < limit) {
          setTotalRecords(currentOffset + objList.length);
        } else {
          // If the API doesn't provide a total count, you can use a dummy total
          // (for example, currentOffset + limit * 10)
          setTotalRecords(currentOffset + limit * 10);
        }
      }
      setIsLoading(false);
    });
  };

  // onSubmit is called when the search form is submitted.
  // It saves the search criteria and fetches the first page (offset 0).
  const onSubmit = (formValues) => {
    formValues.makerDateTimeFrom = formValues.makerDateTimeFrom
      ? BOM.DateTime(formValues.makerDateTimeFrom, "iso-date")
      : null;
    formValues.checkerDateTimeFrom = formValues.checkerDateTimeFrom
      ? BOM.DateTime(formValues.checkerDateTimeFrom, "iso-date")
      : null;
    formValues.makerDateTimeTo = formValues.makerDateTimeTo
      ? BOM.DateTime(formValues.makerDateTimeTo, "iso-date")
      : null;
    formValues.checkerDateTimeTo = formValues.checkerDateTimeTo
      ? BOM.DateTime(formValues.checkerDateTimeTo, "iso-date")
      : null;

    const params = {
      actionName:
        formValues.actionName || formValues.actionName === 0
          ? actionData.find((item) => item.value === formValues.actionName).label
          : null,
      checkerDateTimeFrom: formValues.checkerDateTimeFrom,
      checkerDateTimeTo: formValues.checkerDateTimeTo,
      checkerId: formValues.checkerId,
      entityName:
        formValues.entityName || formValues.entityName === 0
          ? entityData.find((item) => item.value === formValues.entityName).label
          : null,
      limit,
      makerDateTimeFrom: formValues.makerDateTimeFrom,
      makerDateTimeTo: formValues.makerDateTimeTo,
      makerId: formValues.makerId,
      offset: 0,
      paged: true,
      processingResult: formValues.status,
      resourceId: formValues.resourceId,
    };

    // Save the search parameters so they can be used when paginating
    setSearchParams(params);
    // Reset pagination state to the first page
    setCurrentPage(1);
    setOffset(0);

    setIsLoading(true);
    setShowSearch(true);
    fetchSearchAudit(0, params);
  };

  return (
    <MainLayout isEnableRightSidebar={true} onView={handleOnView}>
      <TopContent
        pageTitle={labelConfig.searchAuditTrail}
        pageItems={staticConfig.searchAuditTrailBreadcrumbItems}
      />
      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        <Spin spinning={isLoading} size="large">
          <Form
            onFinish={onSubmit}
            form={useForm}
            layout="vertical"
            style={{ paddingTop: 20, marginBottom: 50 }}
          >
            <Card
              style={{
                marginBottom: "2%",
                boxShadow: "0px 0px 30px 5px rgba(0,0,0,0.025)",
              }}
              bordered
            >
              <Button
                type="primary"
                shape="round"
                size="small"
                className="of-primaryBtnColor"
                onClick={onResetFields}
                icon={<ReloadOutlined />}
                loading={isLoading}
              >
                Reset
              </Button>

              <Row style={{ justifyContent: "space-between" }}>
                <Divider orientation="left" orientationMargin="0">
                  {/* <Title level={5} style={{ margin: 0, marginBottom: 0 }}>
                    {labelConfig.checkedStartPeriod}
                  </Title> */}
                </Divider>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="makerId" label={<Text>{labelConfig.maker}</Text>}>
                    <Select
                      filterOption={(input, option) =>
                        (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())
                      }
                      placeholder={BOM.handlePlaceholderMsg(labelConfig.maker, "select")}
                      options={appUserData}
                      showSearch
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="makerDateTimeFrom" label={<Text>{labelConfig.fromDate}</Text>}>
                    <DatePicker style={{ width: "100%", height: 35 }} />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item
                    name="checkerDateTimeFrom"
                    label={<Text>{labelConfig.checkedFrom}</Text>}
                  >
                    <DatePicker style={{ width: "100%", height: 35 }} />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="status" label={<Text>{labelConfig.status}</Text>}>
                    <Select
                      options={statusData}
                      filterOption={(input, option) =>
                        (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())
                      }
                      placeholder={BOM.handlePlaceholderMsg(labelConfig.status, "select")}
                      showSearch
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="actionName" label={<Text>{labelConfig.action}</Text>}>
                    <Select
                      filterOption={(input, option) =>
                        (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())
                      }
                      placeholder={BOM.handlePlaceholderMsg(labelConfig.action, "select")}
                      options={actionData}
                      showSearch
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="resourceId" label={<Text>{labelConfig.resourceId}</Text>}>
                    <InputNumber
                      placeholder={BOM.handlePlaceholderMsg(labelConfig.resourceId)}
                      style={styles.ofInputFieldNumber}
                    />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="makerDateTimeTo" label={<Text>{labelConfig.toDate}</Text>}>
                    <DatePicker style={{ width: "100%", height: 35 }} />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="checkerDateTimeTo" label={<Text>{labelConfig.checkedTo}</Text>}>
                    <DatePicker style={{ width: "100%", height: 35 }} />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="entityName" label={<Text>{labelConfig.entity}</Text>}>
                    <Select
                      filterOption={(input, option) =>
                        (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())
                      }
                      placeholder={BOM.handlePlaceholderMsg(labelConfig.entity, "select")}
                      options={entityData}
                      showSearch
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col span={8} style={{ padding: "0px 8px" }}>
                  <Form.Item name="checkerId" label={<Text>{labelConfig.checker}</Text>}>
                    <Select
                      filterOption={(input, option) =>
                        (option?.label?.toLowerCase() ?? "").includes(input?.toLowerCase())
                      }
                      placeholder={BOM.handlePlaceholderMsg(labelConfig.checker, "select")}
                      options={appUserData}
                      showSearch
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Show table only when search has been performed */}
              {showSearch && (
                <>
                  <Row style={{ marginBottom: 20 }}>
                    {/* <DownloadTableButtons
                      downloadData={auditTrailList}
                      columns={columns}
                      fileName={"Audit Trail List"}
                    /> */}
                  </Row>
                  <Table
                    rowSelection={{
                      type: "radio",
                      ...rowSelection,
                      onChange: onSelectItemAction,
                    }}
                    columns={columns}
                    dataSource={auditTrailList}
                    bordered
                    pagination={{
                      position: "bottomCentre",
                      current: currentPage,
                      pageSize: limit,
                      total: totalRecords,
                      onChange: (page) => {
                        const newOffset = (page - 1) * limit;
                        setCurrentPage(page);
                        setOffset(newOffset);
                        fetchSearchAudit(newOffset, searchParams);
                      },
                      showSizeChanger: false,
                    }}
                    scroll={{ x: 800 }}
                  />
                </>
              )}
            </Card>
            <Form.Item>
              <Row>
                <CustomButton
                  content="Search"
                  style={{
                    width: "10%",
                    marginLeft: "auto",
                    boxShadow: "0px 0px 10px 5px rgba(0,0,0,0.1)",
                  }}
                  htmlType="submit"
                  backgroundColor="#92428B"
                  colorText="white"
                  hoverBackground="#AD4EA7"
                />
              </Row>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };

export default connect(mapStateToProps, mapDispatchToProps)(SearchAuditTrails);
